import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Alert, Button, Card, Typography, message, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getUserWorkLocationsHelper } from "../../utils/helpers/workLocationsHelper";
import dayjs from "dayjs";
import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import { isSuperAdmin } from "../../utils/permissions";
import { usePermissions } from "../../utils/usePermissions";
import {
  createSupplierCustomValue,
  updateSupplierCustomValue,
  deleteSupplierCustomValue,
  generateMonthlyFixedExpenses,
} from "../../utils/api/supplierExpensesAPI";
import { useFinanceData } from "./hooks/useFinanceData";
import { PeriodSelector } from "./components/PeriodSelector";
import { FinanceFilters } from "./components/FinanceFilters";
import { SupplierTable } from "./components/SupplierTable";
import { AddCustomValueModal } from "./components/AddCustomValueModal";
import { PaymentSummary } from "./components/PaymentSummary";
import { UnpaidExpensesModal } from "./components/UnpaidExpensesModal";
import { EmployeeSalaries } from "./components/EmployeeSalaries";
import { RevenueSummary } from "./components/RevenueSummary";
import { InventorySection } from "./components/InventorySection";
import { isLocationVisibleForPeriod } from "../../utils/helpers/locationVisibility";
import "./Finance.scss";

const { Text } = Typography;

// "Danas" and "Sedmica" are not yet wired up — enable once daily/weekly granularity is live
const QUICK_PRESETS = [/* "Danas", "Sedmica", */ "Mesec", "Kvartal", "Godina"] as const;
type QuickPreset = (typeof QUICK_PRESETS)[number];

const Finance: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const dispatch = useDispatch();
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const [searchParams, setSearchParams] = useSearchParams();
  const filtersRef = useRef<HTMLDivElement>(null);
  const hasUrlLocation = useRef(searchParams.get("locations") !== null);
  const hasUrlCategory = useRef(searchParams.get("category") !== null);
  const initialScrollDoneRef = useRef(false);
  const [highlightCustomValueId] = useState<string | undefined>(
    () => searchParams.get("highlightId") ?? undefined,
  );

  const { hasPermission } = usePermissions();
  const canViewSummary =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-summary");
  const canViewFinancialReview =
    isSuperAdmin(userData?.roles) ||
    hasPermission("finance:view-financial-review");
  const canViewExpenses =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-expenses");
  const canUpdateMatrix =
    isSuperAdmin(userData?.roles) || hasPermission("business:update-expense");
  const canFilterByLocation =
    isSuperAdmin(userData?.roles) ||
    hasPermission("business:filter-by-location");
  const canViewInventory =
    isSuperAdmin(userData?.roles) || hasPermission("finance:view-inventory");
  const canManageInventory =
    isSuperAdmin(userData?.roles) || hasPermission("finance:manage-inventory");

  // State — initialized from URL params when present
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    const months = searchParams.get("months")?.split(",").filter(Boolean);
    return months && months.length > 0 ? months : [dayjs().format("YYYY-MM")];
  });
  const [locationFilters, setLocationFilters] = useState<string[]>(() =>
    searchParams.get("locations")?.split(",").filter(Boolean) ?? [],
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() =>
    searchParams.get("category")?.split(",").filter(Boolean) ?? [],
  );
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(() =>
    searchParams.get("suppliers")?.split(",").filter(Boolean) ?? [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [quickPreset, setQuickPreset] = useState<QuickPreset>("Mesec");
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const [customValueModalVisible, setCustomValueModalVisible] = useState(false);
  const [unpaidExpensesModalVisible, setUnpaidExpensesModalVisible] =
    useState(false);
  const [unpaidModalFilter, setUnpaidModalFilter] = useState<"in-time" | "overdue" | undefined>(undefined);
  const [employeeSalariesLoading, setEmployeeSalariesLoading] = useState(false);
  const [revenueSummaryLoading, setRevenueSummaryLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingCustomValueId, setEditingCustomValueId] = useState<
    string | null
  >(null);
  const [selectedSupplierForCustomValue, setSelectedSupplierForCustomValue] =
    useState<{
      supplierId: string;
      supplierName: string;
      categoryId: string;
      locationId: string;
    } | null>(null);
  const [newCustomValue, setNewCustomValue] = useState({
    description: "",
    amount: 0,
    currency: "RSD" as "RSD" | "EUR",
    month: dayjs().month() + 1,
    year: dayjs().year(),
    invoiceDate: dayjs().format("YYYY-MM-DD"),
    dueDate: "",
  });

  // Load data using custom hook
  const [generatingMonth, setGeneratingMonth] = useState<string | null>(null);
  const [revenueTypes, setRevenueTypes] = useState<{ _id: string; name: string }[]>([]);
  const [goodsClosingStock, setGoodsClosingStock] = useState(0);

  const {
    categories,
    locations,
    defaultValues,
    customValues,
    dailyRevenueExpenses,
    generationStatus,
    loading,
    refetchCustomValues,
    refetchDailyRevenueExpenses,
    refetchGenerationStatus,
  } = useFinanceData(clientId, selectedMonths);

  // Load user work locations and auto-select for non-super admins
  useEffect(() => {
    const loadUserWorkLocations = async () => {
      if (!userData || !clientId) return;

      const userId = userData?._id || userData?.id;
      if (!userId) return;

      try {
        const workLocs = await getUserWorkLocationsHelper(
          userId,
          userData.accessToken,
          dispatch,
          workLocationsState,
          true,
        );
        setUserWorkLocations(workLocs);

        // Auto-select all assigned work locations for non-super admins
        // Skip if locations were already set via URL params
        if (!isSuperAdmin(userData?.roles) && workLocs.length > 0 && !hasUrlLocation.current) {
          const workLocationIds = workLocs.map((wl: any) => wl._id.toString());
          setLocationFilters(workLocationIds);
        }
      } catch (error) {
        console.error("Error loading user work locations:", error);
      }
    };

    loadUserWorkLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.accessToken, clientId]);

  // Fetch revenue types (used as inventory categories)
  useEffect(() => {
    if (!clientId) return;
    axios
      .get(`${BASE_API_URL}/admin/${clientId}/getRevenueTypes`)
      .then((res) => setRevenueTypes(res.data || []))
      .catch(() => {});
  }, [clientId]);

  // Sync filter state → URL params (replace so we don't pollute history)
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedMonths.length > 0) params.set("months", selectedMonths.join(","));
    if (selectedCategories.length > 0) params.set("category", selectedCategories.join(","));
    if (selectedSuppliers.length > 0) params.set("suppliers", selectedSuppliers.join(","));
    if (locationFilters.length > 0) params.set("locations", locationFilters.join(","));
    if (highlightCustomValueId) params.set("highlightId", highlightCustomValueId);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonths, selectedCategories, selectedSuppliers, locationFilters]);

  // Scroll to filters section when arriving from Dashboard with a pre-selected category
  useEffect(() => {
    if (initialScrollDoneRef.current || !hasUrlCategory.current || loading) return;
    initialScrollDoneRef.current = true;
    const timer = setTimeout(() => {
      filtersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Filter locations based on user permissions
  const availableLocations = useMemo(() => {
    const periodLocations = locations.filter((loc: any) =>
      isLocationVisibleForPeriod(loc, selectedMonths),
    );

    if (isSuperAdmin(userData?.roles)) {
      return periodLocations;
    }

    if (userWorkLocations.length > 0) {
      return periodLocations.filter((loc: any) =>
        userWorkLocations.some((wl) => wl._id?.toString() === loc._id?.toString()),
      );
    }

    return periodLocations;
  }, [locations, userWorkLocations, userData?.roles, selectedMonths]);

  // Filter locations to display based on selected filters
  const filteredLocations = useMemo(() => {
    if (locationFilters.length === 0) {
      return availableLocations;
    }
    return availableLocations.filter((loc: any) =>
      locationFilters.includes(loc._id?.toString()),
    );
  }, [availableLocations, locationFilters]);

  // Handle month selection change
  const handleMonthsChange = useCallback((months: string[]) => {
    setSelectedMonths(months);
  }, []);

  // Quick preset handler
  const handleQuickPreset = useCallback((preset: QuickPreset) => {
    setQuickPreset(preset);
    const now = dayjs();
    switch (preset) {
      case "Mesec":
        setSelectedMonths([now.format("YYYY-MM")]);
        break;
      case "Kvartal":
        setSelectedMonths([2, 1, 0].map((i) => now.subtract(i, "month").format("YYYY-MM")));
        break;
      case "Godina":
        setSelectedMonths(Array.from({ length: 12 }, (_, i) => now.subtract(11 - i, "month").format("YYYY-MM")));
        break;
    }
  }, []);

  // Reset all filters to defaults and clear URL params
  const handleResetFilters = useCallback(() => {
    setSelectedMonths([dayjs().format("YYYY-MM")]);
    setSelectedCategories([]);
    setSelectedSuppliers([]);
    setLocationFilters([]);
    setQuickPreset("Mesec");
  }, []);

  // Handle adding/updating custom value
  const handleAddCustomValue = useCallback(async () => {
    if (!selectedSupplierForCustomValue || !clientId) return;
    if (newCustomValue.amount <= 0) {
      message.warning(t("Amount must be greater than 0"));
      return;
    }

    try {
      if (editMode && editingCustomValueId) {
        // Update existing custom value
        await updateSupplierCustomValue(editingCustomValueId, {
          description: newCustomValue.description,
          amount: newCustomValue.amount,
          currency: newCustomValue.currency,
          invoiceDate: newCustomValue.invoiceDate,
          dueDate: newCustomValue.dueDate || undefined,
        });
        message.success(t("Custom value updated successfully"));
      } else {
        // Create new custom value
        await createSupplierCustomValue({
          clientId,
          supplierId: selectedSupplierForCustomValue.supplierId,
          supplierName: selectedSupplierForCustomValue.supplierName,
          locationId: selectedSupplierForCustomValue.locationId,
          month: newCustomValue.month,
          year: newCustomValue.year,
          invoiceDate: newCustomValue.invoiceDate,
          dueDate: newCustomValue.dueDate || undefined,
          description: newCustomValue.description,
          amount: newCustomValue.amount,
          currency: newCustomValue.currency,
          paymentStatus: "unpaid",
        });
        message.success(t("Custom value added successfully"));
      }

      setCustomValueModalVisible(false);
      setEditMode(false);
      setEditingCustomValueId(null);
      setNewCustomValue({
        description: "",
        amount: 0,
        currency: "RSD",
        month: dayjs().month() + 1,
        year: dayjs().year(),
        invoiceDate: dayjs().format("YYYY-MM-DD"),
        dueDate: "",
      });
      setSelectedSupplierForCustomValue(null);
      await refetchCustomValues();
    } catch (err) {
      console.error("Error saving custom value:", err);
      message.error(
        editMode
          ? t("Failed to update custom value")
          : t("Failed to add custom value"),
      );
    }
  }, [
    selectedSupplierForCustomValue,
    clientId,
    newCustomValue,
    editMode,
    editingCustomValueId,
    t,
    refetchCustomValues,
  ]);

  // Handle deleting custom value
  const handleDeleteCustomValue = useCallback(
    async (id: string) => {
      try {
        await deleteSupplierCustomValue(id);
        message.success(t("Custom value deleted"));
        await refetchCustomValues();
      } catch (err) {
        console.error("Error deleting custom value:", err);
        message.error(t("Failed to delete custom value"));
      }
    },
    [t, refetchCustomValues],
  );

  // Handle refreshing supplier data after payment status updates
  const handleRefreshSupplierData = useCallback(async () => {
    await Promise.all([refetchCustomValues(), refetchDailyRevenueExpenses()]);
  }, [refetchCustomValues, refetchDailyRevenueExpenses]);

  // Handle opening modal for adding custom value
  const handleOpenAddModal = useCallback(
    (supplierData: {
      supplierId: string;
      supplierName: string;
      categoryId: string;
      locationId: string;
    }) => {
      setEditMode(false);
      setEditingCustomValueId(null);
      setSelectedSupplierForCustomValue(supplierData);
      setCustomValueModalVisible(true);
    },
    [],
  );

  // Handle opening modal for editing custom value
  const handleOpenEditModal = useCallback(
    (
      customValue: any,
      supplierData: {
        supplierId: string;
        supplierName: string;
        categoryId: string;
        locationId: string;
      },
    ) => {
      setEditMode(true);
      setEditingCustomValueId(customValue._id);
      setSelectedSupplierForCustomValue(supplierData);
      setNewCustomValue({
        description: customValue.description || "",
        amount: customValue.amount,
        currency: customValue.currency,
        month: customValue.month,
        year: customValue.year,
        invoiceDate: customValue.invoiceDate || dayjs().format("YYYY-MM-DD"),
        dueDate: customValue.dueDate || "",
      });
      setCustomValueModalVisible(true);
    },
    [],
  );

  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setCustomValueModalVisible(false);
    setEditMode(false);
    setEditingCustomValueId(null);
    setNewCustomValue({
      description: "",
      amount: 0,
      currency: "RSD",
      month: dayjs().month() + 1,
      year: dayjs().year(),
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      dueDate: "",
    });
    setSelectedSupplierForCustomValue(null);
  }, []);

  // Handle custom value field changes
  const handleCustomValueChange = useCallback(
    (changes: Partial<typeof newCustomValue>) => {
      setNewCustomValue((prev) => ({ ...prev, ...changes }));
    },
    [],
  );

  // Handle location filter change with validation
  const handleLocationFilterChange = useCallback(
    (vals: string[]) => {
      // Prevent deselecting all locations if user has assigned work locations
      if (!isSuperAdmin(userData?.roles) && userWorkLocations.length > 0) {
        if (!vals || vals.length === 0) {
          message.warning(t("At least one location must be selected"));
          return;
        }

        // Ensure user can only select from their assigned work locations
        const workLocationIds = userWorkLocations.map((wl: any) =>
          wl._id.toString(),
        );
        const validSelections = vals.filter((v) => workLocationIds.includes(v));

        if (validSelections.length === 0) {
          message.warning(t("You can only select your assigned locations"));
          return;
        }

        setLocationFilters(validSelections);
      } else {
        // Super admins can select/deselect freely
        setLocationFilters(vals || []);
      }
    },
    [userData?.roles, userWorkLocations, t],
  );

  // Handle supplier selection - auto-select/deselect categories
  const handleSupplierChange = useCallback(
    (supplierIds: string[]) => {
      // Find all categories that should be selected based on the selected suppliers
      const categoriesToSelect = new Set<string>();
      categories.forEach((cat) => {
        const hasSelectedSupplier = cat.suppliers.some((sup) =>
          supplierIds.includes(sup._id),
        );
        if (hasSelectedSupplier) {
          categoriesToSelect.add(cat._id);
        }
      });

      const newCategories = Array.from(categoriesToSelect);

      // Check if we would exceed the 3 category limit
      if (newCategories.length > 3) {
        message.error(
          t("Cannot select suppliers - would exceed maximum of 3 categories"),
        );
        return; // Block the selection
      }

      // Find categories that should be removed (no suppliers selected from them)
      const categoriesToKeep = selectedCategories.filter((catId) => {
        const category = categories.find((c) => c._id === catId);
        if (!category) return false;

        // Keep category if it has any selected supplier
        return category.suppliers.some((sup) => supplierIds.includes(sup._id));
      });

      // Merge: keep existing categories that still have suppliers + add new categories
      const finalCategories = Array.from(
        new Set([...categoriesToKeep, ...newCategories]),
      );

      setSelectedSuppliers(supplierIds);
      setSelectedCategories(finalCategories);
    },
    [categories, selectedCategories, t],
  );

  // Get all unique suppliers from all categories, enriched with any supplier names from custom values
  const allSuppliers = useMemo(() => {
    const supplierMap = new Map<string, { _id: string; name: string; categoryName: string }>();
    categories.forEach((cat) => {
      cat.suppliers.forEach((sup) => {
        if (!supplierMap.has(sup._id)) {
          supplierMap.set(sup._id, { ...sup, categoryName: cat.name });
        }
      });
    });
    // Fill in any suppliers referenced by custom values but not yet in a known category
    customValues.forEach((cv: any) => {
      const id = cv.supplierId ? String(cv.supplierId) : undefined;
      if (id && !supplierMap.has(id) && cv.supplierName) {
        supplierMap.set(id, { _id: id, name: cv.supplierName, categoryName: "—" });
      }
    });
    return Array.from(supplierMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [categories, customValues]);

  const autoExpandSupplierId = useMemo(() => {
    if (!highlightCustomValueId) return undefined;
    const cv = customValues.find((cv: any) => cv._id === highlightCustomValueId);
    return cv?.supplierId ? String(cv.supplierId) : undefined;
  }, [highlightCustomValueId, customValues]);

  // When arriving with highlightId, auto-select the category that contains the supplier
  useEffect(() => {
    if (!highlightCustomValueId || !autoExpandSupplierId || loading) return;
    if (selectedCategories.length > 0) return;
    const cat = categories.find((c: any) =>
      c.suppliers.some((s: any) => String(s._id) === autoExpandSupplierId),
    );
    if (cat) setSelectedCategories([cat._id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCustomValueId, autoExpandSupplierId, categories, loading]);

  // Filter categories and suppliers based on selected filters
  const filteredCategories = useMemo(() => {
    let cats = categories;

    // Search query filters both categories and suppliers
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      cats = cats
        .filter((cat) => {
          // Show category if category name matches OR any supplier name matches
          const categoryMatches = cat.name.toLowerCase().includes(query);
          const hasMatchingSupplier = cat.suppliers.some((sup) =>
            sup.name.toLowerCase().includes(query),
          );
          return categoryMatches || hasMatchingSupplier;
        })
        .map((cat) => {
          // If category name matches, show all suppliers
          if (cat.name.toLowerCase().includes(query)) {
            return cat;
          }
          // Otherwise, filter suppliers by query
          return {
            ...cat,
            suppliers: cat.suppliers.filter((sup) =>
              sup.name.toLowerCase().includes(query),
            ),
          };
        })
        .filter((cat) => cat.suppliers.length > 0);

      return cats;
    }

    // If no search, apply category and supplier filters
    if (selectedCategories.length === 0) {
      return [];
    }

    // Filter by selected categories
    cats = cats.filter((cat) => selectedCategories.includes(cat._id));

    // Filter by selected suppliers if any
    if (selectedSuppliers.length > 0) {
      cats = cats
        .map((cat) => ({
          ...cat,
          suppliers: cat.suppliers.filter((sup) =>
            selectedSuppliers.includes(sup._id),
          ),
        }))
        .filter((cat) => cat.suppliers.length > 0);
    }

    return cats;
  }, [categories, selectedCategories, selectedSuppliers, searchQuery]);

  // Set of "YYYY-MM" strings for months that have had fixed expenses generated
  const generatedMonths = useMemo(
    () => new Set(Object.keys(generationStatus)),
    [generationStatus],
  );

  // Months currently selected that have NOT been generated yet
  const ungeneratedMonths = useMemo(
    () => selectedMonths.filter((m) => !generatedMonths.has(m)),
    [selectedMonths, generatedMonths],
  );

  const handleGenerateFixedExpenses = useCallback(
    async (monthStr: string) => {
      if (!clientId) return;
      const [yearStr, monStr] = monthStr.split("-");
      setGeneratingMonth(monthStr);
      try {
        const result = await generateMonthlyFixedExpenses({
          clientId,
          month: parseInt(monStr),
          year: parseInt(yearStr),
        });
        message.success(
          t("Generated {{count}} fixed expense(s) for {{month}}", {
            count: result.expenseCount,
            month: dayjs(monthStr).format("MMM YYYY"),
          }),
        );
        await Promise.all([refetchCustomValues(), refetchGenerationStatus()]);
      } catch (err: any) {
        if (err?.response?.status === 409) {
          message.info(t("Fixed expenses already generated for this month"));
          await refetchGenerationStatus();
        } else {
          message.error(t("Failed to generate fixed expenses"));
        }
      } finally {
        setGeneratingMonth(null);
      }
    },
    [clientId, t, refetchCustomValues, refetchGenerationStatus],
  );

  // Calculate paid and unpaid expenses totals
  const expenseTotals = useMemo(() => {
    const today = dayjs().startOf("day");
    const totals = {
      paid: { rsd: 0, eur: 0 },
      unpaidInTime: { rsd: 0, eur: 0 },   // not yet overdue (rok nije probijem)
      unpaidOverdue: { rsd: 0, eur: 0 },  // overdue (rok probijen)
      fixedCosts: { rsd: 0, eur: 0 },
    };

    const filteredDefaultValues = defaultValues.filter((dv) => {
      if (!dv.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(dv.locationId);
    });

    const filteredCustomValues = customValues.filter((cv) => {
      if (!cv.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(cv.locationId);
    });

    const filteredDailyExpenses = dailyRevenueExpenses.filter((exp) => {
      if (!exp.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(exp.locationId);
    });

    // Classifies an unpaid amount into inTime or overdue based on dueDate
    const addUnpaid = (
      amount: number,
      currency: "rsd" | "eur",
      dueDate: string | undefined,
    ) => {
      if (dueDate && dayjs(dueDate).startOf("day").isBefore(today)) {
        totals.unpaidOverdue[currency] += amount;
      } else {
        totals.unpaidInTime[currency] += amount;
      }
    };

    filteredDefaultValues.forEach((dv) => {
      const monthKey = dv.month;
      if (monthKey && generatedMonths.has(monthKey)) return;
      totals.paid.rsd += dv.defaultValue;
      totals.fixedCosts.rsd += dv.defaultValue;
    });

    filteredCustomValues.forEach((cv) => {
      const currency = cv.currency.toLowerCase() as "rsd" | "eur";
      if (cv.paymentStatus === "paid") {
        totals.paid[currency] += cv.amount;
      } else if (cv.paymentStatus === "unpaid") {
        addUnpaid(cv.amount, currency, cv.dueDate);
      } else if (cv.paymentStatus === "partial") {
        const paidAmount = cv.partialPayment?.paidAmount || 0;
        const remainingAmount = cv.partialPayment?.remainingAmount || cv.amount;
        totals.paid[currency] += paidAmount;
        addUnpaid(remainingAmount, currency, cv.dueDate);
      }
    });

    filteredDailyExpenses.forEach((exp) => {
      if (exp.paymentStatus === "paid") {
        totals.paid.rsd += exp.amount;
      } else if (exp.paymentStatus === "unpaid") {
        // Daily expenses have no dueDate → always in-time bucket
        totals.unpaidInTime.rsd += exp.amount;
      } else if (exp.paymentStatus === "partial") {
        const paidAmount = exp.partialPayment?.paidAmount || 0;
        const remainingAmount = exp.partialPayment?.remainingAmount || exp.amount;
        totals.paid.rsd += paidAmount;
        totals.unpaidInTime.rsd += remainingAmount;
      }
    });

    return totals;
  }, [customValues, dailyRevenueExpenses, defaultValues, locationFilters, generatedMonths]);

  // Calculate total business expenses by currency (for Revenue Summary)
  const totalBusinessExpenses = useMemo(() => {
    const totals = { rsd: 0, eur: 0 };

    // Filter by selected locations
    const filteredDefaultValues = defaultValues.filter((dv) => {
      if (!dv.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(dv.locationId);
    });

    const filteredCustomValues = customValues.filter((cv) => {
      if (!cv.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(cv.locationId);
    });

    const filteredDailyExpenses = dailyRevenueExpenses.filter((exp) => {
      if (!exp.locationId) return false;
      if (locationFilters.length === 0) return true;
      return locationFilters.includes(exp.locationId);
    });

    // Add default values only for months that have NOT been generated yet
    filteredDefaultValues.forEach((dv) => {
      const monthKey = dv.month;
      if (monthKey && generatedMonths.has(monthKey)) return;
      totals.rsd += dv.defaultValue;
    });

    // Add custom values (keep currencies separate)
    filteredCustomValues.forEach((cv) => {
      const currency = cv.currency.toLowerCase() as "rsd" | "eur";
      totals[currency] += cv.amount;
    });

    // Add daily revenue expenses (always RSD)
    filteredDailyExpenses.forEach((exp) => {
      totals.rsd += exp.amount;
    });

    return totals;
  }, [customValues, dailyRevenueExpenses, defaultValues, locationFilters, generatedMonths]);

  // Check permissions
  if (!canViewExpenses && !isSuperAdmin(userData?.roles)) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">
            {t("You don't have permission to view this page")}
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="finance-page">
      {/* ── Header ── */}
      <div className="fin-head">
        <div>
          <h1 className="fin-title">{t("Finance")}</h1>
          <div className="fin-sub">{t("Manage revenues, supplier values and financial expenses")}</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="fin-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>
          </svg>
          {t("Export report")}
        </button>
      </div>

      {/* ── Filter bar ── */}
      <div className="fin-filters">
        {/* Location */}
        <div className="ff-group">
          <label>{t("Location")}</label>
          <Select
            mode="multiple"
            className="ff-loc-select"
            placeholder={t("All locations")}
            value={locationFilters}
            onChange={handleLocationFilterChange}
            allowClear
            showSearch
            maxTagCount="responsive"
            style={{ minWidth: 200 }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={availableLocations.map((loc: any) => ({ label: loc.name, value: loc._id }))}
          />
        </div>

        {/* Period */}
        <div className="ff-group">
          <label>{t("Period")}</label>
          <PeriodSelector
            selectedMonths={selectedMonths}
            onMonthsChange={handleMonthsChange}
            disabled={employeeSalariesLoading || revenueSummaryLoading}
          />
        </div>

        {/* Quick presets */}
        <div className="ff-group">
          <label>{t("Shortcut")}</label>
          <div className="ff-segment">
            {QUICK_PRESETS.map((p) => (
              <button
                key={p}
                className={quickPreset === p ? "active" : ""}
                onClick={() => handleQuickPreset(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Reset */}
        <button className="ff-reset" onClick={handleResetFilters}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>
          </svg>
          Reset
        </button>
      </div>

      {/* Fixed Expense Generation Banners */}
      {!loading &&
        canUpdateMatrix &&
        ungeneratedMonths.map((monthStr) => (
          <Alert
            key={monthStr}
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={t("Fixed expenses for {{month}} have not been generated yet", {
              month: dayjs(monthStr).format("MMM YYYY"),
            })}
            action={
              <Button
                size="small"
                type="primary"
                loading={generatingMonth === monthStr}
                onClick={() => handleGenerateFixedExpenses(monthStr)}
              >
                {t("Generate")}
              </Button>
            }
          />
        ))}
      {!loading &&
        canUpdateMatrix &&
        selectedMonths
          .filter((m) => generatedMonths.has(m))
          .map((monthStr) => {
            const rec = generationStatus[monthStr];
            const [y, m] = monthStr.split("-").map(Number);
            const liveCount = customValues.filter(
              (cv: any) => cv.type === "fixed" && cv.month === m && cv.year === y,
            ).length;
            return (
              <Alert
                key={monthStr}
                type="success"
                showIcon
                style={{ marginBottom: 8 }}
                message={t(
                  "{{count}} fixed expense(s) generated for {{month}} on {{date}}",
                  {
                    count: liveCount,
                    month: dayjs(monthStr).format("MMM YYYY"),
                    date: dayjs(rec.generatedAt).format("DD.MM.YYYY"),
                  },
                )}
              />
            );
          })}

      {/* Fixed expenses section wrapper */}
      <div className="fixed-section" ref={filtersRef}>
        <FinanceFilters
          categories={categories}
          allSuppliers={allSuppliers}
          selectedCategories={selectedCategories}
          selectedSuppliers={selectedSuppliers}
          searchQuery={searchQuery}
          onCategoryChange={setSelectedCategories}
          onSupplierChange={handleSupplierChange}
          onSearchChange={setSearchQuery}
        />

        {/* Supplier Tables by Category */}
        {loading ? (
          <Card loading={true} />
        ) : canViewExpenses &&
          filteredCategories &&
          filteredCategories.length > 0 ? (
          <div>
            {filteredCategories.map((category) => (
              <Card
                key={category._id}
                className="finance-panel"
                bordered={false}
                style={{ marginBottom: 16 }}
                title={<span style={{ fontSize: 15, fontWeight: 600 }}>{category.name}</span>}
              >
              <SupplierTable
                category={category}
                suppliers={category.suppliers}
                locations={filteredLocations}
                defaultValues={defaultValues}
                customValues={customValues}
                dailyRevenueExpenses={dailyRevenueExpenses}
                canUpdateMatrix={canUpdateMatrix}
                onAddCustomValue={handleOpenAddModal}
                onEditCustomValue={handleOpenEditModal}
                onDeleteCustomValue={handleDeleteCustomValue}
                onRefresh={handleRefreshSupplierData}
                highlightCustomValueId={highlightCustomValueId}
                autoExpandSupplierId={autoExpandSupplierId}
              />
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <Text type="secondary">
            {selectedCategories.length === 0
              ? t("Please select at least one category to view suppliers")
              : searchQuery
                ? t("No suppliers found matching your search")
                : t("No categories found")}
          </Text>
          </Card>
        )}
      </div>{/* end fixed-section */}

      {/* Revenue Summary - Financial Review */}
      {!loading && canViewFinancialReview && (
        <RevenueSummary
          clientId={clientId}
          selectedMonths={selectedMonths}
          locationFilters={locationFilters}
          businessExpenses={totalBusinessExpenses}
          onLoadingChange={setRevenueSummaryLoading}
          defaultValues={defaultValues}
          customValues={customValues}
          dailyExpenses={dailyRevenueExpenses}
          locations={locations}
          categories={categories}
          generationStatus={generationStatus}
          goodsStock={goodsClosingStock}
        />
      )}

      {/* Goods Inventory Section */}
      {!loading && canViewInventory && (
        <InventorySection
          clientId={clientId}
          selectedMonths={selectedMonths}
          revenueTypes={revenueTypes}
          availableLocations={availableLocations}
          locationFilters={locationFilters}
          onStockChange={(total) => setGoodsClosingStock(total)}
          canManage={canManageInventory}
        />
      )}

      {/* Employee Salaries */}
      {!loading && canViewExpenses && (
        <EmployeeSalaries
          clientId={clientId}
          selectedMonths={selectedMonths}
          availableLocations={availableLocations}
          locationFilters={locationFilters}
          sectorFilters={[]}
          onLoadingChange={setEmployeeSalariesLoading}
        />
      )}

      {/* Payment Summary */}
      {!loading && canViewSummary && (
        <PaymentSummary
          paidTotals={expenseTotals.paid}
          unpaidInTimeTotals={expenseTotals.unpaidInTime}
          unpaidOverdueTotals={expenseTotals.unpaidOverdue}
          fixedCostsTotals={expenseTotals.fixedCosts}
          onUnpaidInTimeClick={() => {
            setUnpaidModalFilter("in-time");
            setUnpaidExpensesModalVisible(true);
          }}
          onUnpaidOverdueClick={() => {
            setUnpaidModalFilter("overdue");
            setUnpaidExpensesModalVisible(true);
          }}
        />
      )}

      {/* Add Custom Value Modal */}
      <AddCustomValueModal
        visible={customValueModalVisible}
        supplierData={selectedSupplierForCustomValue}
        locations={availableLocations}
        customValue={newCustomValue}
        editMode={editMode}
        onValueChange={handleCustomValueChange}
        onCancel={handleCloseModal}
        onSubmit={handleAddCustomValue}
      />

      {/* Unpaid Expenses Modal */}
      <UnpaidExpensesModal
        visible={unpaidExpensesModalVisible}
        onClose={() => {
          setUnpaidExpensesModalVisible(false);
          setUnpaidModalFilter(undefined);
        }}
        customValues={customValues}
        dailyRevenueExpenses={dailyRevenueExpenses}
        onRefresh={handleRefreshSupplierData}
        locationFilters={locationFilters}
        selectedMonths={selectedMonths}
        filterMode={unpaidModalFilter}
      />
    </div>
  );
};

export default Finance;
